import { Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@rudi/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@rudi/ui/dialog';
import { useDeletePendingUser } from '../api/hooks';
import type { PendingDirectoryUser } from '../api/types';

function errorMessage(error: unknown): string {
  const response = (
    error as { response?: { status?: number; data?: { detail?: string; error?: string } } }
  )?.response;
  // Rudi from before pending-user deletion answers 404 for the route itself.
  if (response?.status === 404 && !response.data?.detail)
    return 'This Rudi cannot delete pending sign-ups yet. Cancel the invitation on the team page instead.';
  return (
    response?.data?.detail ??
    response?.data?.error ??
    (error instanceof Error ? error.message : 'Delete failed')
  );
}

/**
 * Delete a person who was invited but never signed up: every pending invitation
 * of their email is cancelled, and the account the invitations created goes
 * unless they set it up. The next invitation then starts from a new account.
 */
export function DeletePendingUserDialog({
  person,
  onClose,
}: {
  person: PendingDirectoryUser | null;
  onClose: () => void;
}) {
  const del = useDeletePendingUser();

  const onDelete = async () => {
    if (!person || del.isPending) return;
    try {
      const result = await del.mutateAsync(person.id);
      toast.success(
        result.account_deleted
          ? `${person.email} deleted, with the account the invitation created`
          : `${person.email} deleted`
      );
      onClose();
    } catch (error) {
      toast.error(errorMessage(error));
    }
  };

  return (
    <Dialog open={person !== null} onOpenChange={next => !next && !del.isPending && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Trash2 className="h-5 w-5 text-destructive" />
            Delete pending sign-up
          </DialogTitle>
          <DialogDescription>
            {person
              ? `Every pending invitation of ${person.email} is cancelled, in every tenant. The account the invitation created is deleted unless they already set it up, so the next invitation starts from a clean slate and its link is the only one that works.`
              : ''}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={del.isPending}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={() => void onDelete()} disabled={del.isPending}>
            {del.isPending ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
